import React, { useMemo, useState } from 'react';
import {
  number, exact, arrayOf, string, func, oneOf, oneOfType,
} from 'prop-types';
import styled, { css } from 'styled-components';
import { Flex, Icon } from '@atoms';
import { ReactComponent as Arrow } from '@assets/arrow-down.svg';

const Dropdown = ({ options, onSelectedOptionChange, selectedOption }) => {
  const [areOptionsOpen, setAreOptionsOpen] = useState(false);
  const selectedOptionName = options.find(option => option.id === selectedOption).name;

  const onChangeSelectState = () => {
    setAreOptionsOpen(prevState => !prevState);
  };

  const Options = useMemo(() => options.map(({ id, name }) => (
    <StyledOption
      padding="md"
      className={selectedOption === id ? 'selected' : ''}
      key={id}
      onClick={onSelectedOptionChange(id)}
    >
      {name}
    </StyledOption>
  )), [options, selectedOption]);

  return (
    <StyledSelect justifyStart padding="md" onClick={onChangeSelectState}>
      {selectedOptionName}
      <StyledDropDownIcon SVG={Arrow} width="15px" height="15px" isOpen={areOptionsOpen} />
      {areOptionsOpen && (
        <Wrapper column>
          {Options}
        </Wrapper>
      )}
    </StyledSelect>
  );
};

const StyledSelect = styled(Flex)`
  ${({
    theme: {
      color, typography, spacing,
    },
  }) => css`
    position: relative;
    width: 100%;
    padding: ${spacing.md};
    border: 0px;
    border-bottom: 1px solid ${color.black}; 
    background-color: ${color.white};
    font-family: ${typography.family};
    font-size: ${typography.size.xl2};
    font-weight: ${typography.weight.bold};
    line-height: normal;
    outline: none;
    cursor: pointer;
    position: relative;
    -webkit-tap-highlight-color: transparent;

    .selected {
      background-color: ${color.strawberry};
      color: ${color.white};
    }

    ${Icon} {
      position: absolute;
      right: 10px;
      top: 14px;
    }
  `}
`;

const StyledOption = styled(Flex)`
  ${({ theme: { typography, color } }) => css`
    font-weight: ${typography.weight.bold};
    text-align: left;
    
    :hover {
      background-color: ${color.grey};
    }
  `}
`;

const StyledDropDownIcon = styled(Icon)`
  ${({ isOpen }) => css`
    ${isOpen && 'transform: rotate(180deg);'}
    transition: transform 0.5s;
  `}
`;

const Wrapper = styled(Flex)`
  ${({ theme: { color } }) => css`
    width: 100%;
    position: absolute;
    top: 44px;
    left: 0px;
    border: 1px solid ${color.black};
    background-color: ${color.white};
  `}
`;

Dropdown.defaultProps = {
  selectedOption: null,
};

Dropdown.propTypes = {
  onSelectedOptionChange: func.isRequired,
  selectedOption: oneOfType([oneOf([null]), number]),
  options: arrayOf(exact({
    price: number,
    id: number,
    name: string,
  })).isRequired,
};

export default Dropdown;
